import type { PhysicalProperties } from './physics/materialPhysicalProperties'
import { SCENE_MANIFEST } from './sceneManifest'
import { isTerrainHole } from './terrainGeometry'
import {
  FaceDecal,
  FaceSurfaceText,
  Quat,
  ROBLOX_MATERIALS,
  RobloxFace,
  RobloxMaterial,
  SceneDescriptor,
  SceneObjectDescriptor,
  SceneScriptDescriptor,
  SceneScriptClassName,
  ShapeKind,
  StaticMeshDescriptor,
  TerrainDescriptor,
  Vec3,
  VehicleDescriptor,
} from './types'

const MATERIAL_SET: ReadonlySet<string> = new Set(ROBLOX_MATERIALS)

const isRobloxMaterial = (v: unknown): v is RobloxMaterial =>
  typeof v === 'string' && MATERIAL_SET.has(v)

const SHAPE_SET: ReadonlySet<ShapeKind> = new Set<ShapeKind>([
  'box',
  'ball',
  'cylinder',
  'wedge',
  'cornerWedge',
])

/** 未知・省略時は安全側で 'box' に倒す。 */
const toShapeKind = (v: unknown): ShapeKind =>
  typeof v === 'string' && SHAPE_SET.has(v as ShapeKind) ?
    (v as ShapeKind)
  : 'box'

/**
 * `scripts/rbxl-to-scene.luau` が public/scenes/{missionId}.json に書き出した
 * SceneDescriptor 互換 JSON を読み込んで、ランタイムで使える SceneDescriptor を返す。
 *
 * 404 や JSON 破損は呼び出し側でフォールバックすべきので reject する。
 * 軽い型検証のみで、奥行きのある検証は World 側の `validateSceneDescriptor` に委ねる。
 */
export const loadSceneFromUrl = async (
  url: string,
): Promise<SceneDescriptor> => {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(
      `シーン JSON の取得に失敗しました (${response.status} ${response.statusText}): ${url}`,
    )
  }
  const raw: unknown = await response.json()
  return normalizeScene(raw, url)
}

/**
 * プレビューが配信される静的アセットの URL を、ミッションキーから組み立てる。
 *
 * シーンキー（{slug}_{num}）は配信サイトごとに異なり（online=rb1_2, license=rj1_2 が
 * 同一テンプレを指す）、JSON はテンプレ単位で 1 つだけ生成される。そのため
 * `sceneManifest`（build-scenes が生成）でキー → テンプレ名を解決する。
 * manifest に無いキーはキー名のまま試し、404 のとき呼び出し側がフォールバックする。
 */
export const sceneJsonUrl = (missionKey: string): string => {
  const template = SCENE_MANIFEST[missionKey] ?? missionKey
  return `/scenes/${template}.json`
}

// --- ここから下は JSON → SceneDescriptor の正規化（any/as は使わない型ガード）---

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null

const isFiniteNumber = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v)

const isVec3 = (v: unknown): v is Vec3 =>
  isObject(v) &&
  isFiniteNumber(v.x) &&
  isFiniteNumber(v.y) &&
  isFiniteNumber(v.z)

const toVec3 = (label: string, v: unknown): Vec3 => {
  if (!isVec3(v)) {
    throw new Error(`${label} は { x, y, z } の有限数である必要があります`)
  }
  return { x: v.x, y: v.y, z: v.z }
}

const isQuat = (v: unknown): v is Quat =>
  isObject(v) &&
  isFiniteNumber(v.x) &&
  isFiniteNumber(v.y) &&
  isFiniteNumber(v.z) &&
  isFiniteNumber(v.w)

/** 任意の入力を基準向きクォータニオンへ正規化する（不正・省略時は undefined）。 */
const toOptionalQuat = (v: unknown): Quat | undefined =>
  isQuat(v) ? { x: v.x, y: v.y, z: v.z, w: v.w } : undefined

const FACE_SET: ReadonlySet<RobloxFace> = new Set<RobloxFace>([
  'front',
  'back',
  'right',
  'left',
  'top',
  'bottom',
])

const isRobloxFace = (v: unknown): v is RobloxFace =>
  typeof v === 'string' && FACE_SET.has(v as RobloxFace)

/** decals 配列を正規化する。face/textureUrl が妥当な要素だけ採る（不正は捨てる）。 */
const toOptionalDecals = (v: unknown): FaceDecal[] | undefined => {
  if (!Array.isArray(v)) {
    return undefined
  }
  const decals: FaceDecal[] = []
  for (const raw of v) {
    if (
      !isObject(raw) ||
      !isRobloxFace(raw.face) ||
      typeof raw.textureUrl !== 'string' ||
      raw.textureUrl.length === 0
    ) {
      continue
    }
    decals.push({
      face: raw.face,
      textureUrl: raw.textureUrl,
      studsPerTileU:
        isFiniteNumber(raw.studsPerTileU) && raw.studsPerTileU > 0 ?
          raw.studsPerTileU
        : undefined,
      studsPerTileV:
        isFiniteNumber(raw.studsPerTileV) && raw.studsPerTileV > 0 ?
          raw.studsPerTileV
        : undefined,
      // 画像の透明度（0=不透明〜1=透明）。範囲外は 0..1 へクランプする。
      transparency:
        isFiniteNumber(raw.transparency) && raw.transparency > 0 ?
          Math.min(1, raw.transparency)
        : undefined,
    })
  }
  return decals.length > 0 ? decals : undefined
}

/** surfaceText 配列を正規化する。face/text/color が妥当な要素だけ採る（不正は捨てる）。 */
const toOptionalSurfaceText = (v: unknown): FaceSurfaceText[] | undefined => {
  if (!Array.isArray(v)) {
    return undefined
  }
  const signs: FaceSurfaceText[] = []
  for (const raw of v) {
    if (
      !isObject(raw) ||
      !isRobloxFace(raw.face) ||
      typeof raw.text !== 'string' ||
      raw.text.length === 0 ||
      typeof raw.color !== 'string' ||
      raw.color.length === 0
    ) {
      continue
    }
    signs.push({
      face: raw.face,
      text: raw.text,
      color: raw.color,
      background:
        typeof raw.background === 'string' && raw.background.length > 0 ?
          raw.background
        : undefined,
    })
  }
  return signs.length > 0 ? signs : undefined
}

/** sound フィールドを正規化する（不正・省略時は undefined = 音なし）。 */
const toOptionalSound = (
  v: unknown,
): SceneObjectDescriptor['sound'] | undefined => {
  if (
    !isObject(v) ||
    typeof v.url !== 'string' ||
    v.url.length === 0 ||
    !isFiniteNumber(v.volume)
  ) {
    return undefined
  }
  // Roblox Sound.Volume の範囲 0〜10 に正規化する（setSoundVolume と同じ範囲）。
  return { url: v.url, volume: Math.min(10, Math.max(0, v.volume)) }
}

const SCRIPT_CLASS_SET: ReadonlySet<SceneScriptClassName> =
  new Set<SceneScriptClassName>(['Script', 'LocalScript'])

const isSceneScriptClassName = (v: unknown): v is SceneScriptClassName =>
  typeof v === 'string' && SCRIPT_CLASS_SET.has(v as SceneScriptClassName)

/** scripts 配列を正規化する。不正な要素や自動実行されない ModuleScript は捨てる。 */
const toOptionalScripts = (v: unknown): SceneScriptDescriptor[] | undefined => {
  if (!Array.isArray(v)) {
    return undefined
  }
  const scripts: SceneScriptDescriptor[] = []
  for (const raw of v) {
    if (
      !isObject(raw) ||
      typeof raw.name !== 'string' ||
      raw.name.length === 0 ||
      !isSceneScriptClassName(raw.className) ||
      typeof raw.source !== 'string' ||
      raw.source.length === 0
    ) {
      continue
    }
    scripts.push({
      name: raw.name,
      className: raw.className,
      source: raw.source,
      disabled: raw.disabled === true ? true : undefined,
    })
  }
  return scripts.length > 0 ? scripts : undefined
}

const toOptionalPhysicalProperties = (
  label: string,
  value: unknown,
): PhysicalProperties | undefined => {
  if (value === undefined) return undefined
  if (
    !isObject(value) ||
    !isFiniteNumber(value.friction) ||
    value.friction < 0 ||
    value.friction > 2 ||
    !isFiniteNumber(value.elasticity) ||
    value.elasticity < 0 ||
    value.elasticity > 1 ||
    !isFiniteNumber(value.frictionWeight) ||
    value.frictionWeight < 0 ||
    value.frictionWeight > 100 ||
    !isFiniteNumber(value.elasticityWeight) ||
    value.elasticityWeight < 0 ||
    value.elasticityWeight > 100
  ) {
    throw new Error(
      `${label} は有限の物理特性（摩擦 0〜2、反発 0〜1、重み 0〜100）である必要があります`,
    )
  }
  return {
    friction: value.friction,
    elasticity: value.elasticity,
    frictionWeight: value.frictionWeight,
    elasticityWeight: value.elasticityWeight,
  }
}

const toObject = (
  label: string,
  raw: unknown,
  index: number,
): SceneObjectDescriptor => {
  if (!isObject(raw)) {
    throw new Error(`${label}[${index}] はオブジェクトである必要があります`)
  }
  const id = raw.id
  const name = raw.name
  const color = raw.color
  if (typeof id !== 'string' || id.length === 0) {
    throw new Error(`${label}[${index}].id は非空文字列である必要があります`)
  }
  if (typeof name !== 'string' || name.length === 0) {
    throw new Error(`${label}[${index}].name は非空文字列である必要があります`)
  }
  if (typeof color !== 'string') {
    throw new Error(`${label}[${index}].color は文字列である必要があります`)
  }
  return {
    id,
    name,
    shape: toShapeKind(raw.shape),
    position: toVec3(`${label}[${index}].position`, raw.position),
    size: toVec3(`${label}[${index}].size`, raw.size),
    color,
    material: isRobloxMaterial(raw.material) ? raw.material : undefined,
    physicalProperties: toOptionalPhysicalProperties(
      `${label}[${index}].physicalProperties`,
      raw.physicalProperties,
    ),
    meshUrl:
      typeof raw.meshUrl === 'string' && raw.meshUrl.length > 0 ?
        raw.meshUrl
      : undefined,
    meshScale:
      isObject(raw.meshScale) ?
        toVec3(`${label}[${index}].meshScale`, raw.meshScale)
      : undefined,
    meshOffset:
      isObject(raw.meshOffset) ?
        toVec3(`${label}[${index}].meshOffset`, raw.meshOffset)
      : undefined,
    textureUrl:
      typeof raw.textureUrl === 'string' && raw.textureUrl.length > 0 ?
        raw.textureUrl
      : undefined,
    textureCutout: raw.textureCutout === true ? true : undefined,
    rotationY:
      typeof raw.rotationY === 'number' && Number.isFinite(raw.rotationY) ?
        raw.rotationY
      : undefined,
    orientation: toOptionalQuat(raw.orientation),
    isStatic: raw.isStatic === true,
    dynamicAtBirth: raw.dynamicAtBirth === true ? true : undefined,
    dynamicOnUnanchor: raw.dynamicOnUnanchor === true ? true : undefined,
    transparency:
      (
        typeof raw.transparency === 'number' &&
        Number.isFinite(raw.transparency)
      ) ?
        raw.transparency
      : undefined,
    canCollide:
      typeof raw.canCollide === 'boolean' ? raw.canCollide : undefined,
    canTouch: typeof raw.canTouch === 'boolean' ? raw.canTouch : undefined,
    decals: toOptionalDecals(raw.decals),
    surfaceText: toOptionalSurfaceText(raw.surfaceText),
    sound: toOptionalSound(raw.sound),
    sparkle: raw.sparkle === true ? true : undefined,
    fire: raw.fire === true ? true : undefined,
    tags:
      (
        Array.isArray(raw.tags) &&
        raw.tags.length > 0 &&
        raw.tags.every((t): t is string => typeof t === 'string')
      ) ?
        raw.tags
      : undefined,
    scripts: toOptionalScripts(raw.scripts),
  }
}

/** 有限数のみの配列かを検証する（terrain の heights/materials 用）。 */
const isFiniteNumberArray = (v: unknown): v is number[] =>
  Array.isArray(v) && v.every(isFiniteNumber)

/**
 * terrain フィールドを正規化する。寸法・配列長の不整合は地形なし（undefined）
 * へ倒す（地形は背景でありミッション進行を止めるほどではないため、シーン全体
 * の読み込み失敗にはしない）。
 */
const toOptionalTerrain = (v: unknown): TerrainDescriptor | undefined => {
  if (!isObject(v)) {
    return undefined
  }
  const { cellSize, originX, originZ, width, depth, heights, materials } = v
  if (
    !isFiniteNumber(cellSize) ||
    cellSize <= 0 ||
    !isFiniteNumber(originX) ||
    !isFiniteNumber(originZ) ||
    !isFiniteNumber(width) ||
    !isFiniteNumber(depth) ||
    !Number.isInteger(width) ||
    !Number.isInteger(depth) ||
    width <= 0 ||
    depth <= 0
  ) {
    return undefined
  }
  if (
    !isFiniteNumberArray(heights) ||
    !isFiniteNumberArray(materials) ||
    heights.length !== width * depth ||
    materials.length !== width * depth
  ) {
    return undefined
  }
  if (!Array.isArray(v.palette)) {
    return undefined
  }
  const palette: TerrainDescriptor['palette'] = []
  for (const entry of v.palette) {
    if (
      !isObject(entry) ||
      !isRobloxMaterial(entry.material) ||
      typeof entry.color !== 'string'
    ) {
      return undefined
    }
    palette.push({ material: entry.material, color: entry.color })
  }
  if (
    materials.some(
      (materialIndex, index) =>
        !isTerrainHole(heights[index]) &&
        (!Number.isInteger(materialIndex) ||
          materialIndex < 0 ||
          materialIndex >= palette.length),
    )
  ) {
    return undefined
  }
  let water: TerrainDescriptor['water']
  if (isObject(v.water)) {
    const { level, x0, x1, z0, z1 } = v.water
    if (
      isFiniteNumber(level) &&
      isFiniteNumber(x0) &&
      isFiniteNumber(x1) &&
      isFiniteNumber(z0) &&
      isFiniteNumber(z1)
    ) {
      water = { level, x0, x1, z0, z1 }
    }
  }
  return {
    cellSize,
    originX,
    originZ,
    width,
    depth,
    heights: [...heights],
    materials: [...materials],
    palette,
    water,
    voxels: toOptionalTerrainVoxels(v.voxels, palette.length),
  }
}

/**
 * terrain.voxels（等値面用ボクセルグリッド）を正規化する。
 * 不正なら undefined（高さマップへフォールバック）。RLE のラン個数の合計が
 * グリッド体積と一致し、素材参照が palette の範囲内であることまで検証する
 * （壊れた RLE で等値面生成が暴走しないようにする）。
 */
const toOptionalTerrainVoxels = (
  v: unknown,
  paletteSize: number,
): TerrainDescriptor['voxels'] => {
  if (!isObject(v)) {
    return undefined
  }
  const { minX, minY, minZ, sizeX, sizeY, sizeZ, runs } = v
  if (
    ![minX, minY, minZ, sizeX, sizeY, sizeZ].every(
      (n) => isFiniteNumber(n) && Number.isInteger(n),
    ) ||
    !isFiniteNumber(sizeX) ||
    !isFiniteNumber(sizeY) ||
    !isFiniteNumber(sizeZ) ||
    sizeX <= 0 ||
    sizeY <= 0 ||
    sizeZ <= 0
  ) {
    return undefined
  }
  if (!isFiniteNumberArray(runs) || runs.length % 3 !== 0) {
    return undefined
  }
  let total = 0
  for (let i = 0; i < runs.length; i += 3) {
    const count = runs[i]
    const material = runs[i + 1]
    const occupancy = runs[i + 2]
    if (
      !Number.isInteger(count) ||
      count <= 0 ||
      !Number.isInteger(material) ||
      material < 0 ||
      material > paletteSize ||
      !Number.isInteger(occupancy) ||
      occupancy < 0 ||
      occupancy > 255
    ) {
      return undefined
    }
    total += count
  }
  if (
    !isFiniteNumber(minX) ||
    !isFiniteNumber(minY) ||
    !isFiniteNumber(minZ) ||
    total !== sizeX * sizeY * sizeZ
  ) {
    return undefined
  }
  return { minX, minY, minZ, sizeX, sizeY, sizeZ, runs: [...runs] }
}

/**
 * staticMeshes 配列を正規化する。不正な要素は捨てる（背景ジオメトリであり、
 * シーン全体の読み込み失敗にはしない）。
 */
const toOptionalStaticMeshes = (
  v: unknown,
): StaticMeshDescriptor[] | undefined => {
  if (!Array.isArray(v)) {
    return undefined
  }
  const meshes: StaticMeshDescriptor[] = []
  for (const raw of v) {
    if (
      !isObject(raw) ||
      typeof raw.name !== 'string' ||
      typeof raw.color !== 'string' ||
      !isFiniteNumberArray(raw.positions) ||
      !isFiniteNumberArray(raw.indices) ||
      raw.positions.length % 3 !== 0 ||
      raw.indices.length % 3 !== 0 ||
      raw.indices.length === 0
    ) {
      continue
    }
    const vertexCount = raw.positions.length / 3
    if (
      !raw.indices.every(
        (i) => Number.isInteger(i) && i >= 0 && i < vertexCount,
      )
    ) {
      continue
    }
    meshes.push({
      name: raw.name,
      color: raw.color,
      material: isRobloxMaterial(raw.material) ? raw.material : undefined,
      positions: [...raw.positions],
      indices: [...raw.indices],
    })
  }
  return meshes.length > 0 ? meshes : undefined
}

/** 非空文字列のみからなる長さ 1 以上の配列かを検証する（vehicles の partIds 用）。 */
const isNonEmptyStringArray = (v: unknown): v is string[] =>
  Array.isArray(v) &&
  v.length > 0 &&
  v.every((s) => typeof s === 'string' && s.length > 0)

/**
 * vehicles 配列を正規化する。不正な要素は捨てる（車両は補助的な遊び要素で
 * あり、シーン全体の読み込み失敗にはしない）。seatId と partIds が揃わない
 * 車両は乗車も移動もできないため要素ごと捨て、exitOffset / maxSpeed /
 * reverseSpeed は物理側の既定値で補えるためフィールド単位で undefined に倒す。
 */
const toOptionalVehicles = (v: unknown): VehicleDescriptor[] | undefined => {
  if (!Array.isArray(v)) {
    return undefined
  }
  const vehicles: VehicleDescriptor[] = []
  for (const raw of v) {
    if (
      !isObject(raw) ||
      typeof raw.seatId !== 'string' ||
      raw.seatId.length === 0 ||
      !isNonEmptyStringArray(raw.partIds)
    ) {
      continue
    }
    vehicles.push({
      seatId: raw.seatId,
      partIds: [...raw.partIds],
      exitOffset:
        isVec3(raw.exitOffset) ?
          { x: raw.exitOffset.x, y: raw.exitOffset.y, z: raw.exitOffset.z }
        : undefined,
      maxSpeed:
        isFiniteNumber(raw.maxSpeed) && raw.maxSpeed > 0 ?
          raw.maxSpeed
        : undefined,
      reverseSpeed:
        isFiniteNumber(raw.reverseSpeed) && raw.reverseSpeed > 0 ?
          raw.reverseSpeed
        : undefined,
    })
  }
  return vehicles.length > 0 ? vehicles : undefined
}

const normalizeScene = (raw: unknown, sourceUrl: string): SceneDescriptor => {
  if (!isObject(raw)) {
    throw new Error(
      `シーン JSON はオブジェクトである必要があります: ${sourceUrl}`,
    )
  }
  const objectsRaw = raw.objects
  if (!Array.isArray(objectsRaw)) {
    throw new Error(
      `シーン JSON の objects は配列である必要があります: ${sourceUrl}`,
    )
  }
  const objects = objectsRaw.map((o, i) => toObject('objects', o, i))

  const avatarRaw = raw.avatar
  const avatar =
    isObject(avatarRaw) ?
      {
        spawn: toVec3('avatar.spawn', avatarRaw.spawn),
        size: toVec3('avatar.size', avatarRaw.size),
        speed:
          (
            typeof avatarRaw.speed === 'number' &&
            Number.isFinite(avatarRaw.speed)
          ) ?
            avatarRaw.speed
          : 12.8,
        color:
          typeof avatarRaw.color === 'string' ? avatarRaw.color : '#2e86de',
        spawnYaw:
          (
            typeof avatarRaw.spawnYaw === 'number' &&
            Number.isFinite(avatarRaw.spawnYaw)
          ) ?
            avatarRaw.spawnYaw
          : undefined,
      }
    : undefined

  const cameraRaw = raw.camera
  const camera =
    isObject(cameraRaw) ?
      {
        position: toVec3('camera.position', cameraRaw.position),
        target: toVec3('camera.target', cameraRaw.target),
      }
    : undefined

  return {
    missionId: typeof raw.missionId === 'string' ? raw.missionId : '',
    title: typeof raw.title === 'string' ? raw.title : '',
    objects,
    avatar,
    camera,
    terrain: toOptionalTerrain(raw.terrain),
    staticMeshes: toOptionalStaticMeshes(raw.staticMeshes),
    // BGM は効果音（sound）と同じ {url, volume} 形式（不正・省略時は BGM なし）。
    bgm: toOptionalSound(raw.bgm),
    lighting: toOptionalLighting(raw.lighting),
    vehicles: toOptionalVehicles(raw.vehicles),
  }
}

/**
 * lighting フィールドを正規化する。clockTime は 0〜24 の時刻（範囲外は 0〜24 へ
 * mod する）。不正・省略時は undefined（既定の昼として描画する）。
 */
const toOptionalLighting = (
  v: unknown,
): SceneDescriptor['lighting'] | undefined => {
  if (!isObject(v) || !isFiniteNumber(v.clockTime)) {
    return undefined
  }
  const clockTime = ((v.clockTime % 24) + 24) % 24
  return { clockTime }
}
